import currentExecutingScript from 'current-executing-script';

// set the public path to be the path of the main entry script
__webpack_public_path__ = // eslint-disable-line
  currentExecutingScript()
    .src.split('?')[0]
    .split('/')
    .slice(0, -1)
    .join('/') + '/';

// setup api
import '@/api';

import { createApp } from 'vue';
import store from './vuex/store';
import router from './vue/router';
import { sync } from 'vuex-router-sync';

import { registerFilters } from './vue/filters';


// Vue components
import WishlistToggle from './vue/components/Wishlist/WishlistToggle.vue';
import { mountDeparturesLazy } from './modules/mount-departures-lazy.ts';
import DataLayer from './vue/components/DataLayer.vue';
import OpenOrClose from './vue/components/OpenOrClose.vue';
import EventBus from './vue/EventBus';

// Standard JS modules
import './getInTouch.ts';
import * as accordion from './modules/accordion.ts';
import * as cookieBot from './modules/cookie-bot.ts';
import * as helpers from './modules/helpers.ts';
import * as siteNavigation from './modules/primary-nav.ts';
import * as siteSearch from './modules/site-search';
import * as video from './modules/video';
import * as responsiveTable from './modules/responsive-table.ts';
import * as scrollTop from './modules/scroll-top.ts';
import * as activeState from './modules/active-state';
import { cardCarousel } from './modules/carousels/card-carousel';
import { responsiveCarousel } from './modules/carousels/responsive-carousel';
import { initAllRgsCarousels } from './modules/carousels/rgs/rgs-carousel';
import { initRgsToursCarousel, initRgsImagesCarousel } from './modules/carousels/rgs/rgs-tours-carousel';
import * as tourPage from './modules/tour-page.ts';
import {
  initSalesforceCollectDeferred,
  initInfinityTrackingDeferred,
  initTrustpilotBootstrapDeferred
} from './modules/third-party-defer.ts';
import { initTelephoneNumberSync } from './modules/telephone-number-sync.ts';
import { initPhoneClickTracking } from './modules/phone-click-tracking.ts';
import * as dropdownToggles from './modules/dropdown-toggles.ts';
import * as readMoreToggle from './modules/read-more-toggle.ts';
import * as anchorScroll from './modules/anchor-scroll.ts';
import './modules/carousels/hero-carousel';
// AB-TEST(homepage-search-input) START
import * as homepageSearchInputABTesting from './modules/homepage-search-input-a-b-testing';
// AB-TEST(homepage-search-input) END

// utils
import * as collectionsDropdown from './modules/utils/collections-dropdown.ts';
import './modules/utils/jump-to-section.ts';

import gtm, * as GTM from './modules/gtm';
import { initUtmPersistence } from './modules/utm-persistence';
import { consumePendingConversion } from './modules/pending-conversion';
initUtmPersistence();
// Emit any conversion staged before a redirect (e.g. brochure request → thank-you page).
consumePendingConversion();
gtm.setupVuexSubscribe();
window.GTM = GTM;

// focus top field when error occurs
import inputErrorFocus from './modules/inputErrorFocus';
inputErrorFocus(document.body);

const mountApp = (el, componentMap, options = {}) => {
  const app = createApp({
    components: componentMap,
    template: el.outerHTML
  });

  if (options.store) app.use(options.store);
  if (options.router) app.use(options.router);
  registerFilters(app);

  const wrapper = document.createElement('div');
  el.parentNode.insertBefore(wrapper, el);
  el.remove();
  app.mount(wrapper);
  return app;
};

const domReady = (fn) => {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn);
  } else {
    fn();
  }
};

// scripts.js is deferred, so domReady runs synchronously after module evaluation and the whole
// init became one ~400ms long task on mobile. Yielding between phases lets the browser paint and
// handle input in between; order is unchanged.
const yieldToMain = () => (window.scheduler?.yield
  ? window.scheduler.yield()
  : new Promise(resolve => setTimeout(resolve, 0)));

domReady(async function() {
  // Data Layer — before the first yield, so view_page is pushed during script evaluation and always
  // precedes `load`. Where scheduler.yield is missing (Safari) the yields are setTimeout tasks, `load`
  // can fire between them, and the load/5s path in deferred-third-party-init.ts would inject GTM first.
  const elDataLayer = document.querySelectorAll('data-layer');
  if (elDataLayer.length) {
    elDataLayer.forEach(el => {
      mountApp(el, { 'data-layer': DataLayer }, { store, router });
    });
  }

  await yieldToMain();

  // AB-TEST(homepage-search-input) START
  homepageSearchInputABTesting.initHomepageSearchInputABTesting();
  // AB-TEST(homepage-search-input) END

  initSalesforceCollectDeferred();
  initInfinityTrackingDeferred();
  initTrustpilotBootstrapDeferred();

  // Live chat (when widget is on page)
  if (document.getElementById('launchChatButton') && window.liveChatConfig?.sfBootstrapUrl) {
    import(/* webpackChunkName: "liveChat" */ './modules/live-chat.ts')
      .then((m) => m.init(GTM))
      .catch((err) => {
        console.error('[liveChat] chunk failed to load', err);
      });
  }

  // Checkout vue (separate chunk — not on initial load for other pages)
  const elCheckout = document.querySelector('checkout');
  if (elCheckout) {
    import(/* webpackChunkName: "checkout" */ '@/vue/components/Checkout/checkoutEntry')
      .then(({ default: Checkout }) => {
        if (!elCheckout.isConnected) return;
        mountApp(elCheckout, {
          checkout: Checkout
        }, { store });
      })
      .catch((err) => {
        console.error('[checkout] chunk failed to load', err);
      });
  }

  // Account vue (chunk: router, shell, + MMB SCSS — not on every page)
  const elAccount = document.querySelector('account');
  if (elAccount) {
    import(/* webpackChunkName: "account" */ '@/vue/components/Account/accountEntry')
      .then((mod) => {
        if (!elAccount.isConnected) return;
        const Account = mod.default;
        const accountRouter = mod.accountRouter;

        sync(store, accountRouter);

        const app = createApp({
          components: { account: Account },
          template: elAccount.outerHTML
        });
        app.use(store);
        app.use(accountRouter);
        registerFilters(app);
        const wrapper = document.createElement('div');
        elAccount.parentNode.insertBefore(wrapper, elAccount);
        elAccount.remove();
        app.mount(wrapper);
      })
      .catch((err) => {
        console.error('[account] chunk failed to load', err);
      });
  }

  const elLogin = document.querySelector('login');
  const elRegister = document.querySelector('register');
  const elForgotPassword = document.querySelector('forgot-password');
  const elResetPassword = document.querySelector('reset-password');

  if (elLogin || elRegister || elForgotPassword || elResetPassword) {
    import(/* webpackChunkName: "auth" */ '@/vue/components/Login/loginEntry')
      .then((mod) => {
        if (elLogin && elLogin.isConnected) {
          mountApp(elLogin, { login: mod.Login }, { store });
        }

        if (elRegister && elRegister.isConnected) {
          mountApp(elRegister, { register: mod.Register }, { store });
        }

        if (elForgotPassword && elForgotPassword.isConnected) {
          mountApp(elForgotPassword, { 'forgot-password': mod.ForgotPassword }, { store });
        }

        if (elResetPassword && elResetPassword.isConnected) {
          mountApp(elResetPassword, { 'reset-password': mod.ResetPassword }, { store });
        }
      })
      .catch((err) => {
        console.error('[auth] chunk failed to load', err);
      });
  }

  // Filter index — lazy chunk (listing pages only)
  const elFilter = document.querySelector('filter-index');
  if (elFilter) {
    import(/* webpackChunkName: "filterIndex" */ './vue/components/FilterIndex/filterIndexEntry')
      .then((mod) => {
        if (!elFilter.isConnected) return;
        mountApp(elFilter, {
          'filter-index': mod.default,
          'filter-index-item': mod.FilterIndexItem
        }, { store, router });
      })
      .catch((err) => {
        console.error('[filterIndex] chunk failed to load', err);
      });
  }

  // home search vue form with autocomplete - lazy loaded
  const elHomeSearch = document.querySelectorAll('home-search');
  if (elHomeSearch.length) {
    import(/* webpackChunkName: "home-search" */ './vue/components/HomeSearch/homeSearchEntry')
      .then(({ default: HomeSearch }) => {
        elHomeSearch.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'home-search': HomeSearch }, { store });
        });
      })
      .catch((err) => {
        console.error('[home-search] chunk failed to load', err);
      });
  }

  // Holiday search modal — deferred until first open-search-modal (header on every page)
  const elHolidaySearchModal = document.querySelectorAll('holiday-search-modal');
  if (elHolidaySearchModal.length) {
    let holidaySearchModalReady = null;
    let holidaySearchModalOpenQueued = false;

    const ensureHolidaySearchModal = () => {
      if (!holidaySearchModalReady) {
        holidaySearchModalReady = import(/* webpackChunkName: "holidaySearchModal" */ './vue/components/HolidaySearch/holidaySearchModalEntry')
          .then(({ default: HolidaySearchModal }) => {
            elHolidaySearchModal.forEach(el => {
              if (!el.isConnected || el.dataset.vueMounted === '1') return;
              el.dataset.vueMounted = '1';
              mountApp(el, { 'holiday-search-modal': HolidaySearchModal }, { store });
            });
          })
          .catch((err) => {
            holidaySearchModalReady = null;
            console.error('[holidaySearchModal] chunk failed to load', err);
          });
      }
      return holidaySearchModalReady;
    };

    EventBus.on('open-search-modal', () => {
      if (holidaySearchModalOpenQueued) return;
      const alreadyMounted = [...elHolidaySearchModal].some(el => el.dataset.vueMounted === '1');
      if (alreadyMounted) return;

      holidaySearchModalOpenQueued = true;
      ensureHolidaySearchModal().then(() => {
        holidaySearchModalOpenQueued = false;
        // Re-emit so the newly mounted modal's listener opens it.
        EventBus.emit('open-search-modal');
      });
    });
  }

  // wishlist — lazy-loaded chunk (page layout CSS only; icons stay in main)
  const elWishlist = document.querySelector('wishlist');
  if (elWishlist) {
    import(/* webpackChunkName: "wishlist" */ './vue/components/Wishlist/wishlistEntry')
      .then(({ default: Wishlist }) => {
        if (!elWishlist.isConnected) return;
        mountApp(elWishlist, { wishlist: Wishlist }, { router });
      })
      .catch((err) => {
        console.error('[wishlist] chunk failed to load', err);
      });
  }

  // Tour add-ons + excursions — shared lazy chunk (PDP)
  const elAddOnList = document.querySelectorAll('add-ons');
  const elExcursionList = document.querySelectorAll('excursions');
  if (elAddOnList.length || elExcursionList.length) {
    import(/* webpackChunkName: "tourLists" */ './vue/components/Tour/tourListsEntry')
      .then(({ AddOnList, ExcursionList }) => {
        elAddOnList.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'add-ons': AddOnList }, { store, router });
        });
        elExcursionList.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'excursions': ExcursionList }, { store, router });
        });
      })
      .catch((err) => {
        console.error('[tourLists] chunk failed to load', err);
      });
  }

  // <wishlist-toggle colour="pink" item="{}" wishlist-cookie-name="blah"></wishlist-toggle>
  const elWishlistToggle = document.querySelectorAll('wishlist-toggle');
  if (elWishlistToggle.length) {
    elWishlistToggle.forEach(el => {
      mountApp(el, { 'wishlist-toggle': WishlistToggle }, {});
    });
  }

  // departures — lazy: deferred until viewport proximity, hash, or "Book Now" click
  const elDepartures = document.querySelector('departures');
  if (elDepartures) {
    let departureMounted = false;
    let departureMountPromise = null;

    const startDeparturesMount = () => {
      if (departureMounted) return;
      if (departureMountPromise) return;
      if (!elDepartures.isConnected) return;

      departureMountPromise = import(/* webpackChunkName: "departures" */ './vue/components/Tour/departuresEntry')
        .then(({ default: Departures }) => {
          if (departureMounted || !elDepartures.isConnected) return;

          const app = createApp({
            components: { departures: Departures },
            template: elDepartures.outerHTML
          });
          app.use(router);
          registerFilters(app);

          const wrapper = document.createElement('div');
          // Transfer the id so any in-progress smooth-scroll keeps its target in the DOM
          if (elDepartures.id) {
            wrapper.id = elDepartures.id;
            elDepartures.removeAttribute('id');
          }
          elDepartures.parentNode.insertBefore(wrapper, elDepartures);
          elDepartures.remove();
          app.mount(wrapper);

          departureMounted = true;
        })
        .catch((err) => {
          console.error('[departures] chunk failed to load', err);
        })
        .finally(() => {
          departureMountPromise = null;
        });
    };

    mountDeparturesLazy(elDepartures, startDeparturesMount);
  }

  // Order Brochure — lazy-loaded chunk (CSS + JS + Vuex only when <brochure-request> is on the page)
  const elBrochureRequest = document.querySelectorAll('brochure-request');
  if (elBrochureRequest.length) {
    import(/* webpackChunkName: "brochureRequest" */ './vue/components/Brochure/brochureRequestEntry')
      .then(({ default: BrochureRequest }) => {
        elBrochureRequest.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'brochure-request': BrochureRequest }, { store, router });
        });
      })
      .catch(err => console.error('[brochureRequest] chunk failed to load', err));
  }

  // Tours by price — lazy-loaded chunk (JS + Vuex; no dedicated SCSS)
  const elToursByPrice = document.querySelector('tours-by-price');
  if (elToursByPrice) {
    import(/* webpackChunkName: "toursByPrice" */ './vue/components/ToursByPrice/toursByPriceEntry')
      .then(({ default: ToursByPrice }) => {
        if (!elToursByPrice.isConnected) return;
        mountApp(elToursByPrice, { 'tours-by-price': ToursByPrice }, { store, router });
      })
      .catch((err) => {
        console.error('[toursByPrice] chunk failed to load', err);
      });
  }

  // Vue carousel islands — shared lazy chunk (block pages only).
  // carousel-block.scss stays global for hero / RGS markup.
  const elSwiperCarousel = document.querySelectorAll('swiper-carousel');
  const elTourStyleIconsCarousel = document.querySelectorAll('tour-style-icons-carousel');
  const elTourHotelCarousel = document.querySelectorAll('tour-hotel-carousel');
  const elFullWidthCarousel = document.querySelectorAll('full-width-carousel');
  const elGalleryBlockCarousel = document.querySelectorAll('gallery-block-carousel');
  if (
    elSwiperCarousel.length ||
    elTourStyleIconsCarousel.length ||
    elTourHotelCarousel.length ||
    elFullWidthCarousel.length ||
    elGalleryBlockCarousel.length
  ) {
    import(/* webpackChunkName: "vueCarousels" */ './vue/components/Carousels/carouselsEntry')
      .then(({
        SwiperCarousel,
        TourStyleIconsCarousel,
        TourHotelCarousel,
        FullWidthCarousel,
        GalleryBlockCarousel
      }) => {
        elSwiperCarousel.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'swiper-carousel': SwiperCarousel }, { store });
        });
        elTourStyleIconsCarousel.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'tour-style-icons-carousel': TourStyleIconsCarousel }, { store });
        });
        elTourHotelCarousel.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'tour-hotel-carousel': TourHotelCarousel }, { store });
        });
        elFullWidthCarousel.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'full-width-carousel': FullWidthCarousel }, {});
        });
        elGalleryBlockCarousel.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'gallery-block-carousel': GalleryBlockCarousel }, {});
        });
      })
      .catch((err) => {
        console.error('[vueCarousels] chunk failed to load', err);
      });
  }

  // Good To Go — lazy chunk
  const elGoodToGo = document.querySelectorAll('goodtogo');
  if (elGoodToGo.length) {
    import(/* webpackChunkName: "goodToGo" */ './vue/components/GoodToGo/goodToGoEntry')
      .then(({ default: GoodToGo }) => {
        elGoodToGo.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'goodtogo': GoodToGo }, {});
        });
      })
      .catch((err) => {
        console.error('[goodToGo] chunk failed to load', err);
      });
  }

  // Customer Moments Reviews — lazy-loaded chunk (JS + CSS + Vuex)
  const elCustomerMomentsReviews = document.querySelectorAll('customer-moments-reviews');
  if (elCustomerMomentsReviews.length) {
    import(/* webpackChunkName: "customerMoments" */ './vue/components/CustomerMoments/customerMomentsEntry')
      .then(({ default: CustomerMomentsReviews }) => {
        elCustomerMomentsReviews.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'customer-moments-reviews': CustomerMomentsReviews }, { store });
        });
      })
      .catch((err) => {
        console.error('[customerMoments] chunk failed to load', err);
      });
  }

  // TrustPilot Reviews — lazy-loaded chunk (CSS + JS only requested when the
  // <trust-pilot-reviews> custom element exists on the page).
  const elTrustPilotReviews = document.querySelectorAll('trust-pilot-reviews');
  if (elTrustPilotReviews.length) {
    import(/* webpackChunkName: "trustPilot" */ './vue/components/TrustPilot/trustPilotEntry')
      .then(({ default: TrustPilotReviews }) => {
        elTrustPilotReviews.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'trust-pilot-reviews': TrustPilotReviews }, { store });
        });
      })
      .catch((err) => {
        console.error('[trustPilot] chunk failed to load', err);
      });
  }

  // ContentPod — lazy-loaded chunk (CSS + JS only requested when the
  // <content-pod-block> custom element exists on the page). Renders the
  // TrustPilot mini-widget internally as a child component.
  const elContentPod = document.querySelectorAll('content-pod-block');
  if (elContentPod.length) {
    import(/* webpackChunkName: "contentPod" */ './vue/components/ContentPod/contentPodEntry')
      .then(({ default: ContentPod }) => {
        elContentPod.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'content-pod-block': ContentPod }, {});
        });
      })
      .catch((err) => {
        console.error('[contentPod] chunk failed to load', err);
      });
  }

  // Embedded Search Content Box — lazy-loaded chunk (JS + CSS + Vuex)
  const elEmbeddedSearchContentBox = document.querySelectorAll('embedded-search-content-box');
  if (elEmbeddedSearchContentBox.length) {
    import(/* webpackChunkName: "embeddedSearchContentBox" */ './vue/components/EmbeddedSearchContentBox/embeddedSearchContentBoxEntry')
      .then(({ default: EmbeddedSearchContentBox }) => {
        elEmbeddedSearchContentBox.forEach(el => {
          if (!el.isConnected) return;
          mountApp(el, { 'embedded-search-content-box': EmbeddedSearchContentBox }, { store });
        });
      })
      .catch((err) => {
        console.error('[embeddedSearchContentBox] chunk failed to load', err);
      });
  }

  await yieldToMain();

  const elOpenOrClose = document.querySelectorAll('open-or-close');
  if (elOpenOrClose.length) {
    elOpenOrClose.forEach(el => {
      mountApp(el, { 'open-or-close': OpenOrClose }, {});
    });
  }

  // Region selector banner — lazy chunk, deferred until idle.
  const elRegionBanner = document.querySelector('region-selector-banner');
  if (elRegionBanner) {
    const loadRegionBanner = () => {
      import(/* webpackChunkName: "regionBanner" */ './vue/components/RegionSelector/RegionSelectorBanner.vue')
        .then(({ default: RegionSelectorBanner }) => {
          if (!elRegionBanner.isConnected) return;
          mountApp(elRegionBanner, { 'region-selector-banner': RegionSelectorBanner }, {});
        })
        .catch((err) => {
          console.error('[regionBanner] chunk failed to load', err);
        });
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(loadRegionBanner, { timeout: 2000 });
    } else {
      setTimeout(loadRegionBanner, 1);
    }
  }

  // Region selector (footer) — dedicated component, lazy chunk, deferred until idle.
  const elRegionSelectorFooter = document.querySelectorAll('region-selector-footer');
  if (elRegionSelectorFooter.length) {
    const loadRegionSelectorFooter = () => {
      import(/* webpackChunkName: "regionSelectorFooter" */ './vue/components/RegionSelector/RegionSelectorFooter.vue')
        .then(({ default: RegionSelectorFooter }) => {
          elRegionSelectorFooter.forEach(el => {
            if (!el.isConnected) return;
            mountApp(el, { 'region-selector-footer': RegionSelectorFooter }, {});
          });
        })
        .catch((err) => {
          console.error('[regionSelectorFooter] chunk failed to load', err);
        });
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(loadRegionSelectorFooter, { timeout: 2000 });
    } else {
      setTimeout(loadRegionSelectorFooter, 1);
    }
  }

  // Content search sidebar — lazy chunk, gated on the ContentSearch:SidebarEnabled flag.
  // When the flag is off the header emits .js-search instead, so there are no triggers here
  // and neither the chunk nor the warm-up costs anything.
  const elContentSearch = document.querySelector('content-search-sidebar');
  const searchTriggers = document.querySelectorAll('.js-open-content-search');
  if (elContentSearch && searchTriggers.length) {
    let contentSearchReady = null;
    const loadContentSearch = () => {
      if (!contentSearchReady) {
        contentSearchReady = import(/* webpackChunkName: "contentSearchSidebar" */ './vue/components/ContentSearchSidebar/contentSearchSidebarEntry')
          .then(({ default: ContentSearchSidebar }) => {
            if (!elContentSearch.isConnected) return;
            mountApp(elContentSearch, { 'content-search-sidebar': ContentSearchSidebar }, { store });
          })
          .catch((err) => {
            contentSearchReady = null;
            console.error('[contentSearchSidebar] chunk failed to load', err);
          });
      }
      return contentSearchReady;
    };

    loadContentSearch();

    searchTriggers.forEach(trigger => {
      trigger.addEventListener('click', (e) => {
        e.preventDefault();
        loadContentSearch().then(() => {
          if (store.hasModule('content-search')) {
            store.dispatch('content-search/openSidebar');
          }
        });
      });
    });
  }

  await yieldToMain();

  accordion.init();
  cookieBot.init();
  helpers.init();

  let lightboxFirstLoad = null;
  const onLightboxIntent = (e) => {
    if (!(e.target instanceof Element)) return;
    const anchor = e.target.closest('a.js-modal[href]');
    if (!anchor) return;
    const href = anchor.getAttribute('href');
    if (!href || href === '#' || href.startsWith('javascript:')) return;

    if (lightboxFirstLoad === 'loading') {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();

    lightboxFirstLoad = 'loading';

    import(/* webpackChunkName: "lightbox" */ './modules/lightbox.ts')
      .then((m) => m.init())
      .then(() => {
        document.removeEventListener('click', onLightboxIntent, true);
        lightboxFirstLoad = 'ready';
        requestAnimationFrame(() => {
          anchor.click();
        });
      })
      .catch((err) => {
        lightboxFirstLoad = null;
        console.error('[lightbox] chunk failed to load', err);
      });
  };
  document.addEventListener('click', onLightboxIntent, true);

  siteNavigation.init();
  siteSearch.init();
  video.init();
  responsiveTable.init();
  scrollTop.init();
  activeState.init();

  await yieldToMain();

  cardCarousel.init();
  responsiveCarousel.init();
  initAllRgsCarousels();
  initRgsToursCarousel();
  initRgsImagesCarousel();
  tourPage.init();
  dropdownToggles.init();
  readMoreToggle.init();
  anchorScroll.init();
  collectionsDropdown.init();

  await yieldToMain();

  initTelephoneNumberSync();
  initPhoneClickTracking();
});
